import { type AddressRollout, AGENT_MODE_CONFIG, FUN_DEPOSITS_CONFIG } from "@decibeltrade/utils";
import { AddressRolloutSchema } from "@decibeltrade/utils/schemas";
import { z } from "zod";

import { env } from "@/env";
import { Logger } from "@/utils/logger";

import {
  type ConfigSpecsResponse,
  fetchConfigSpecs,
  getDynamicConfigValue,
} from "./statsig-server";

const STATSIG_DYNAMIC_CONFIG = {
  MAINTENANCE_MODE: "maintenance_mode",
  GLOBAL_ANNOUNCEMENT: "global_announcement",
  MARKET_STATUS_OVERRIDES: "market_status_overrides",
  ENCRYPTION_CONFIG: "encryption_config",
  AGENT_MODE: AGENT_MODE_CONFIG,
  AGENT_MODE_CAPS: "agent_mode_caps",
  CCTP_V2: "cctp_v2",
  FUN_DEPOSITS: FUN_DEPOSITS_CONFIG,
  CARD_VAULT: "card_vault",
} as const;

const bannerSeveritySchema = z.enum(["info", "warning", "critical"]);

export type BannerSeverity = z.infer<typeof bannerSeveritySchema>;

const maintenanceModeSchema = z.object({
  enabled: z.boolean(),
  message: z.string().optional(),
});

const globalAnnouncementSchema = z.object({
  enabled: z.boolean(),
  message: z.string(),
  severity: bannerSeveritySchema,
  dismissible: z.boolean(),
  id: z.string(),
});

const marketStatusOverrideSchema = z.object({
  enabled: z.boolean(),
  message: z.string(),
  severity: bannerSeveritySchema,
});

// Per-network encryption rollout block. `enabled` is the master switch;
// `rolloutPercentage` (0-100) is the share of devices that get encryption,
// bucketed deterministically by device id so a user's experience is stable as
// the percentage ramps. `allowedAddresses` force-includes specific wallets in
// the cohort regardless of bucket (still subject to `enabled`). The full config
// is keyed by network name; each deploy reads only its own network's block.
const encryptionNetworkConfigSchema = z.object({
  enabled: z.boolean(),
  rolloutPercentage: z.number().min(0).max(100),
  allowedAddresses: z.array(z.string()).default([]),
});

export type EncryptionNetworkConfig = z.infer<typeof encryptionNetworkConfigSchema>;

export type FunDepositsConfig = AddressRollout;

// Deliberately the server's own caps block: a client-side copy would drift from the 413.
const DEFAULT_MAX_INPUT_LENGTH = 4_096;

const agentModeCapsNetworkSchema = z.object({
  maxInputLength: z.number().finite().positive().catch(DEFAULT_MAX_INPUT_LENGTH),
});

export type AgentModeCapsConfig = z.infer<typeof agentModeCapsNetworkSchema>;

export type CctpDirection = "deposit" | "withdraw";

// Same rollout shape as agent_mode, one block per direction, keyed by network like
// encryption_config: { mainnet: { deposit: {...}, withdraw: {...} }, testnet: {...} }.
const cctpV2DirectionSchema = z.object({
  enabled: z.boolean(),
  allowedAddresses: z.array(z.string()).default([]),
  rolloutPercent: z.number().int().min(0).max(100).catch(0),
});

export type CctpV2DirectionConfig = z.infer<typeof cctpV2DirectionSchema>;

/**
 * The `cctp_v2` config resolved for this deploy's network. `null` for a direction means Statsig
 * gave no usable value for it, so callers use the code fallback instead.
 */
export interface CctpV2Config {
  deposit: CctpV2DirectionConfig | null;
  withdraw: CctpV2DirectionConfig | null;
}

const NO_CCTP_V2_CONFIG: CctpV2Config = { deposit: null, withdraw: null };

/** Each direction parses on its own, so a bad edit to one can't switch the other. */
export function parseCctpV2Config(raw: Record<string, unknown>): CctpV2Config {
  const block = raw[env.NEXT_PUBLIC_SDK_CONFIG_NAME];
  if (block === undefined) return NO_CCTP_V2_CONFIG;
  if (typeof block !== "object" || block === null) {
    console.error("[statsig] cctp_v2 block for this network is not an object");
    return NO_CCTP_V2_CONFIG;
  }

  const parseDirection = (direction: CctpDirection): CctpV2DirectionConfig | null => {
    const value = (block as Record<string, unknown>)[direction];
    if (value === undefined) return null;
    const result = cctpV2DirectionSchema.safeParse(value);
    if (!result.success) {
      console.error("[statsig] failed to parse cctp_v2.%s:", direction, result.error.message);
      return null;
    }
    return result.data;
  };

  return { deposit: parseDirection("deposit"), withdraw: parseDirection("withdraw") };
}

const cardVaultSchema = z.object({ enabled: z.boolean() });

export type CardVaultConfig = z.infer<typeof cardVaultSchema>;

export interface MaintenanceModeResult {
  isMaintenanceMode: boolean;
  message?: string;
}

export type GlobalAnnouncementConfig = z.infer<typeof globalAnnouncementSchema>;

export interface MarketStatusOverride {
  enabled: boolean;
  message: string;
  severity: BannerSeverity;
}

export interface AllStatsigConfigs {
  maintenanceMode: MaintenanceModeResult;
  globalAnnouncement: GlobalAnnouncementConfig | null;
  marketStatusOverrides: Record<string, MarketStatusOverride>;
  /** Encryption rollout block resolved for the current network. */
  encryption: EncryptionNetworkConfig;
  agentMode: AddressRollout;
  /** Server caps block resolved for the current network. */
  agentModeCaps: AgentModeCapsConfig;
  cctpV2: CctpV2Config;
  funDeposits: FunDepositsConfig;
  cardVault: CardVaultConfig;
}

// Fail safe: when Statsig is unreachable or the config is missing, encryption is
// off everywhere.
const DISABLED_ENCRYPTION: EncryptionNetworkConfig = {
  enabled: false,
  rolloutPercentage: 0,
  allowedAddresses: [],
};

const ROLLOUT_OFF: AddressRollout = {
  enabled: false,
  allowedAddresses: [],
  rolloutPercent: 0,
};

const DEFAULTS: AllStatsigConfigs = {
  maintenanceMode: { isMaintenanceMode: false },
  globalAnnouncement: null,
  marketStatusOverrides: {},
  encryption: DISABLED_ENCRYPTION,
  agentMode: ROLLOUT_OFF,
  agentModeCaps: { maxInputLength: DEFAULT_MAX_INPUT_LENGTH },
  cctpV2: NO_CCTP_V2_CONFIG,
  funDeposits: ROLLOUT_OFF,
  // Fail closed: Card Vault stays hidden unless the Statsig config opts in.
  cardVault: { enabled: false },
};

export const DEFAULT_STATSIG_CONFIGS = DEFAULTS;

/** Fetch all Statsig configs in a single call using the CDN config specs endpoint. */
export async function getAllStatsigConfigs(): Promise<AllStatsigConfigs> {
  try {
    const specs = await fetchConfigSpecs();

    if (!specs) return DEFAULTS;

    reportAbsentConfigs(specs);

    const maintenanceMode = parseMaintenanceMode(
      getDynamicConfigValue(specs, STATSIG_DYNAMIC_CONFIG.MAINTENANCE_MODE),
    );
    const globalAnnouncement = parseGlobalAnnouncement(
      getDynamicConfigValue(specs, STATSIG_DYNAMIC_CONFIG.GLOBAL_ANNOUNCEMENT),
    );
    const marketStatusOverrides = parseMarketStatusOverrides(
      getDynamicConfigValue(specs, STATSIG_DYNAMIC_CONFIG.MARKET_STATUS_OVERRIDES),
    );
    const encryption = parseEncryptionConfig(
      getDynamicConfigValue(specs, STATSIG_DYNAMIC_CONFIG.ENCRYPTION_CONFIG),
    );
    const agentMode = parseAddressRollout(specs, STATSIG_DYNAMIC_CONFIG.AGENT_MODE);
    const agentModeCaps = parseAgentModeCaps(
      getDynamicConfigValue(specs, STATSIG_DYNAMIC_CONFIG.AGENT_MODE_CAPS),
    );
    const cctpV2 = parseCctpV2Config(getDynamicConfigValue(specs, STATSIG_DYNAMIC_CONFIG.CCTP_V2));
    const funDeposits = parseAddressRollout(specs, STATSIG_DYNAMIC_CONFIG.FUN_DEPOSITS);
    const cardVault = parseCardVaultConfig(
      getDynamicConfigValue(specs, STATSIG_DYNAMIC_CONFIG.CARD_VAULT),
    );

    return {
      maintenanceMode,
      globalAnnouncement,
      marketStatusOverrides,
      encryption,
      agentMode,
      agentModeCaps,
      cctpV2,
      funDeposits,
      cardVault,
    };
  } catch {
    return DEFAULTS;
  }
}

const EXPECTED_CONFIG_NAMES = Object.values(STATSIG_DYNAMIC_CONFIG);

let loggedAbsentConfigs = false;

// A total miss implicates the key; partial absence is the documented not-yet-created state.
function reportAbsentConfigs(specs: ConfigSpecsResponse): void {
  if (loggedAbsentConfigs) return;
  const present = new Set(specs.dynamic_configs.map((c) => c.name));
  const missing = EXPECTED_CONFIG_NAMES.filter((name) => !present.has(name));
  if (missing.length < EXPECTED_CONFIG_NAMES.length) return;
  loggedAbsentConfigs = true;
  Logger.error(
    new Error(
      "[statsig] dynamic configs absent from the Statsig project; STATSIG_SERVER_KEY may point at the wrong project",
    ),
    { extra: { missing } },
  );
}

function parseMaintenanceMode(raw: Record<string, unknown>): MaintenanceModeResult {
  const result = maintenanceModeSchema.safeParse(raw);
  if (!result.success) {
    console.error("[statsig] failed to parse maintenance_mode config:", result.error.message);
    return DEFAULTS.maintenanceMode;
  }
  return { isMaintenanceMode: result.data.enabled, message: result.data.message };
}

function parseGlobalAnnouncement(raw: Record<string, unknown>): GlobalAnnouncementConfig | null {
  const result = globalAnnouncementSchema.safeParse(raw);
  if (!result.success) {
    console.error("[statsig] failed to parse global_announcement config:", result.error.message);
    return DEFAULTS.globalAnnouncement;
  }
  if (!result.data.enabled || !result.data.message) return null;
  return result.data;
}

export function parseCardVaultConfig(raw: Record<string, unknown>): CardVaultConfig {
  const block = raw[env.NEXT_PUBLIC_SDK_CONFIG_NAME];
  if (block === undefined) return DEFAULTS.cardVault;
  const result = cardVaultSchema.safeParse(block);
  if (!result.success) {
    Logger.error(new Error("[statsig] failed to parse card_vault config"), {
      extra: { zodError: result.error.message },
    });
    return DEFAULTS.cardVault;
  }
  return result.data;
}

function parseMarketStatusOverrides(
  raw: Record<string, unknown>,
): Record<string, MarketStatusOverride> {
  const result = z.record(z.string(), marketStatusOverrideSchema).safeParse(raw);
  if (!result.success) {
    console.error(
      "[statsig] failed to parse market_status_overrides config:",
      result.error.message,
    );
    return DEFAULTS.marketStatusOverrides;
  }
  return result.data;
}

// The raw config is keyed by network name (e.g. { mainnet: {...}, testnet: {...} }).
// Validate ONLY this deploy's network block, not the whole record — otherwise a
// malformed sibling entry (e.g. a future "devnet" block missing a field) would
// fail the parse for every network and disable encryption everywhere. An absent
// block means encryption simply isn't configured for this network yet; a present
// but malformed block for this network degrades to encryption-disabled.
function parseEncryptionConfig(raw: Record<string, unknown>): EncryptionNetworkConfig {
  const block = raw[env.NEXT_PUBLIC_SDK_CONFIG_NAME];
  if (block === undefined) return DISABLED_ENCRYPTION;
  const result = encryptionNetworkConfigSchema.safeParse(block);
  if (!result.success) {
    console.error("[statsig] failed to parse encryption_config:", result.error.message);
    return DISABLED_ENCRYPTION;
  }
  return result.data;
}

function parseAddressRollout(specs: ConfigSpecsResponse, configName: string): AddressRollout {
  const block = getDynamicConfigValue(specs, configName)[env.NEXT_PUBLIC_SDK_CONFIG_NAME];
  if (block === undefined) return ROLLOUT_OFF;
  const result = AddressRolloutSchema.safeParse(block);
  if (!result.success) {
    console.error("[statsig] failed to parse %s config:", configName, result.error.message);
    return ROLLOUT_OFF;
  }
  return result.data;
}

// Mirrors the server's caps reader: a stricter client would ignore a shape it accepts.
function parseAgentModeCaps(raw: Record<string, unknown>): AgentModeCapsConfig {
  const block = raw[env.NEXT_PUBLIC_SDK_CONFIG_NAME];
  const isNested = typeof block === "object" && block !== null && !Array.isArray(block);
  const result = agentModeCapsNetworkSchema.safeParse(isNested ? block : raw);
  if (!result.success) {
    console.error("[statsig] failed to parse agent_mode_caps config:", result.error.message);
    return DEFAULTS.agentModeCaps;
  }
  return result.data;
}
